'use client';

import { useCallback, useEffect } from 'react';
import { useSetAtom } from 'jotai';

import { WishlistSkusDocument } from '@packages/gql/generated/shopping/WishlistSkusDocument';
import { useAuthQuery } from '@packages/shared/src/hooks/useAuthQuery/useAuthQuery';
import { useEnsureFreshData } from '@packages/shared/src/hooks/useEnsureFreshData/useEnsureFreshData';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useSession } from '@packages/shared/src/hooks/useSession/useSession';

import { wishlistLoadingAtom, wishlistSkusAtom } from './wishlist.atoms';

/* GraphQL */ `
  query WishlistSkus($locale: String!) {
    wishlist(locale: $locale) {
      lineItems {
        sku
      }
      count
    }
  }
`;

export const wishlistUpdatedEventName = 'wishlistUpdated';

export const WishlistSkusSync = () => {
  const { locale } = useI18n();
  const { jwtToken } = useSession();
  const setSkus = useSetAtom(wishlistSkusAtom);
  const setLoading = useSetAtom(wishlistLoadingAtom);

  const [{ data, fetching }, executeQuery] = useAuthQuery({
    query: WishlistSkusDocument,
    variables: { locale },
    requestPolicy: 'cache-and-network',
  });

  const refetch = useCallback(
    () => executeQuery({ requestPolicy: 'network-only' }),
    [executeQuery],
  );

  useEffect(() => {
    setLoading(!jwtToken || fetching);
  }, [jwtToken, fetching, setLoading]);

  useEffect(() => {
    if (!data || fetching) return;
    setSkus(new Set(data.wishlist?.lineItems.map((item) => item.sku) ?? []));
  }, [data, fetching, setSkus]);

  useEnsureFreshData({ refetch });

  useEffect(() => {
    window.addEventListener(wishlistUpdatedEventName, refetch);
    return () => {
      window.removeEventListener(wishlistUpdatedEventName, refetch);
    };
  }, [refetch]);

  return null;
};
